import { useState } from 'react';
import { IoFolderOutline, IoChevronForward } from 'react-icons/io5';
import { MdMoreVert } from 'react-icons/md';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import moment from 'moment';
import ProjectFolderMenu from '@ProjectFunctionalDisplay/ProjectFolderMenu';
import './ProjectFolderRow.scss';

export default function ProjectFolderRow(props) {

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [showMenu, setShowMenu] = useState(false);
    const [contextMenu, setContextMenu] = useState(null);
    const className = 'projectFolderRow';

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setShowMenu(true);
        setContextMenu(null); // Close context menu if open
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Only show context menu for folders that have actions
        if (props.folderId || props.folder) {
            setContextMenu({
                mouseX: event.clientX + 2,
                mouseY: event.clientY - 6,
            });
            setShowMenu(false); // Close regular menu if open
        }
    };

    const handleMenuClose = (event) => {
        if (event) {
            event.stopPropagation();
        }
        setMenuAnchor(null);
        setShowMenu(false);
    };

    const handleContextMenuClose = (event) => {
        if (event) {
            event.stopPropagation();
        }
        setContextMenu(null);
    };

    const handleRowClick = (event) => {
        // Don't navigate if clicking on action buttons
        if (event.target.closest('.MuiIconButton-root')) {
            return;
        }
        if (props.handleFolderClick) {
            props.handleFolderClick();
        }
    };

    const getFolderTypeLabel = () => {
        switch (props.folderType) {
            case 'lists':
                return 'Lists';
            case 'tickets':
                return 'Tickets';
            case 'projectFiles':
                return 'Files';
            case 'exports':
                return 'Exports';
            case 'kaiziqManageConnection':
                return 'Connection';
            case 'ticketFolderList':
                return 'Ticket';
            default:
                return 'Folder';
        }
    };

    const getItemCount = () => {
        if (props.folder?.fileCount !== undefined) {
            return `${props.folder.fileCount} items`;
        }
        return null;
    };

    const getModifiedDate = () => {
        if (props.folder?.modifiedTimestamp) {
            return moment(props.folder.modifiedTimestamp).format('MMM DD, YYYY');
        }
        return null;
    };

    return (
        <Box 
            className={className} 
            onClick={handleRowClick}
            onContextMenu={handleContextMenu}>
            <Box className={className + '__container'}>
                {/* Left Section - Icon & Name */}
                <Box className={className + '__left'}>
                    <Box className={className + '__icon'}>
                        {props.icon || <IoFolderOutline />}
                    </Box>
                    <Box className={className + '__info'}>
                        <Typography variant="subtitle2" className={className + '__name'}>
                            {props.folderName}
                        </Typography>
                        {getItemCount() && (
                            <Typography variant="caption" className={className + '__count'}>
                                {getItemCount()}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Middle Section - Type & Date */}
                <Box className={className + '__middle'}>
                    <Typography variant="caption" className={className + '__type'}>
                        {getFolderTypeLabel()}
                    </Typography>
                    {getModifiedDate() && (
                        <Typography variant="caption" className={className + '__date'}>
                            {getModifiedDate()}
                        </Typography>
                    )}
                </Box>

                {/* Right Section - Actions */}
                <Box className={className + '__right'}>
                    <Tooltip title="Open" arrow placement="left" slots={{ transition: Zoom }}>
                        <IconButton 
                            className={className + '__open'}
                            size="small"
                            onClick={handleRowClick}>
                            <IoChevronForward />
                        </IconButton>
                    </Tooltip>

                    {(props.folderId || props.folder) && (
                        <>
                            <Tooltip title="More" arrow placement="left" slots={{ transition: Zoom }}>
                                <IconButton 
                                    className={className + '__menu'}
                                    size="small"
                                    onClick={handleMenuOpen}>
                                    <MdMoreVert />
                                </IconButton>
                            </Tooltip>

                            {/* Regular Menu (Three Dots) */}
                            {showMenu && (
                                <ProjectFolderMenu
                                    anchorEl={menuAnchor}
                                    open={showMenu}
                                    onClose={handleMenuClose}
                                    folderId={props.folderId}
                                    folder={props.folder}
                                    navigationParentId={props.navigationParentId}
                                    renderData={props.renderData}
                                />
                            )}

                            {/* Context Menu (Right Click) */}
                            {contextMenu !== null && (
                                <ProjectFolderMenu
                                    anchorReference="anchorPosition"
                                    anchorPosition={
                                        contextMenu !== null
                                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                                            : undefined
                                    }
                                    open={contextMenu !== null}
                                    onClose={handleContextMenuClose}
                                    folderId={props.folderId}
                                    folder={props.folder}
                                    navigationParentId={props.navigationParentId}
                                    renderData={props.renderData}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
